import { useMutation } from '@tanstack/react-query'
import { fetchESignData } from '../repository/MutualFundDetail'
import { useEffect, useRef } from 'react'
import { handleClose, postCTA } from '../utils/appBridge'
import { captureException } from '@sentry/react'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { logESignEvent, ESignLogMessage, ESignLogLevel, markESignPageReached } from '../utils/utils'
import { EventNames } from '../types/misc.types'
import { trackEvent } from '../utils/appBridge'

const MAX_RETRIES = 6
const API_TIMEOUT_MS = 12000

// Utility functions
const logException = (error: string, errorType: string, extra: Record<string, any> = {}) => {
  captureException(new Error(error), {
    tags: {
      component: 'ESignPage',
      errorType
    },
    extra: {
      ...extra,
      url: window.location.href,
      timestamp: new Date().toISOString()
    }
  })
}

const clearTimeoutSafely = (timeoutId: NodeJS.Timeout | undefined) => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
}

// Component functions
const handleApiSuccess = (data: any, onError: (error: string) => void) => {
  const ctaData = data?.cta || data?.data?.cta
  if (ctaData) {
    postCTA(JSON.stringify(ctaData))
  } else {
    onError('no cta from response')
  }
}

const handleApiError = (error: any, retryCount: number, onRetry: () => void, onError: (error: string) => void) => {
  const statusCode = error?.response?.status || 'unknown'
  const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error'
  const errorString = `API failed ${statusCode}: ${errorMessage}`

  if (retryCount < MAX_RETRIES) {
    onRetry()
  } else {
    onError(errorString)
  }
}

// Removed handleTimeout function since it's now handled inline

const createErrorHandler = (retryCountRef: React.MutableRefObject<number>) => {
  return (errString: string) => {
    logException(errString, 'api_failure', {
      retryCount: retryCountRef.current
    })
    handleClose()
  }
}

const createRetryHandler = (retryCountRef: React.MutableRefObject<number>, mutate: () => void) => {
  return () => {
    retryCountRef.current += 1
    mutate()
  }
}

// Main component
export default function EsignPage() {
  const retryCountRef = useRef(0)
  const apiCallStartedRef = useRef(false)
  const timeoutIdRef = useRef<NodeJS.Timeout>()

  // Critical: Mark that ESignPage has been reached and log component creation
  markESignPageReached()
  logESignEvent(ESignLogMessage.ESIGN_PAGE_CREATED)

  const handleError = createErrorHandler(retryCountRef)

  const { mutate, isPending, isError, isSuccess } = useMutation({
    mutationFn: fetchESignData,
    onSuccess: (data) => {
      clearTimeoutSafely(timeoutIdRef.current)
      logESignEvent(ESignLogMessage.ESIGN_API_SUCCESS, ESignLogLevel.INFO, {
        hasData: !!data,
        hasCta: !!(data?.cta || data?.data?.cta)
      })
      handleApiSuccess(data, handleError)
    },
    onError: (error: any) => {
      clearTimeoutSafely(timeoutIdRef.current)
      const statusCode = error?.response?.status || 'unknown'
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error'

      logESignEvent(ESignLogMessage.ESIGN_API_FAILED, ESignLogLevel.ERROR, {
        statusCode,
        errorMessage,
        retryCount: retryCountRef.current,
        maxRetries: MAX_RETRIES,
        willRetry: retryCountRef.current < MAX_RETRIES
      })

      const handleRetry = createRetryHandler(retryCountRef, mutate)
      handleApiError(error, retryCountRef.current, handleRetry, handleError)
    }
  })

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        eventId: 'esign_reached',
        screenType: 'MF_ESign_PAGE'
      }
    })
    apiCallStartedRef.current = true

    logESignEvent(ESignLogMessage.ESIGN_PAGE_MOUNTED)

    timeoutIdRef.current = setTimeout(() => {
      if (apiCallStartedRef.current && !isSuccess && !isError) {
        logESignEvent(ESignLogMessage.ESIGN_API_TIMEOUT, ESignLogLevel.ERROR, {
          isPending: true,
          isError,
          isSuccess,
          retryCount: retryCountRef.current,
          timeoutMs: API_TIMEOUT_MS
        })
      }
    }, API_TIMEOUT_MS)

    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        eventId: 'esign_called',
        screenType: 'MF_ESign_PAGE'
      }
    })

    mutate()

    return () => {
      clearTimeoutSafely(timeoutIdRef.current)
    }
  }, [])

  // Render logic
  if (isPending) {
    return <LoadingSpinner />
  }

  if (isError) {
    return (
      <div className='h-screen overflow-y-scroll no-scrollbar'>
        <div className='flex min-h-screen items-center flex-col justify-center text-red-500 p-10'>
          <p className='text-center'>
            Something went wrong. <br /> Please try again.
          </p>
        </div>
      </div>
    )
  }

  if (isSuccess) {
    return <LoadingSpinner />
  }

  return <LoadingSpinner />
}
